import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { Period, Reference, TimeAggregate, TimeSlot } from '@sonar/models';

import { chartRequested } from '../Entities/actions';
import {
  areAverageSpentsLoading,
  getAverageSpentsChartData,
  getAverageSpentsError,
} from '../Entities/AverageSpents/selectors';
import {
  areCustomersCountsLoading,
  getCustomersCountsChartData,
  getCustomersCountsError,
} from '../Entities/CustomersCounts/selectors';
import {
  areRevenuesLoading,
  getRevenuesChartData,
  getRevenuesError,
} from '../Entities/Revenues/selectors';
import { KPITab } from '../Preferences';

import { tabToChartKPIMapping } from './constants';

interface UseLegacyOrdersAggregatedParam {
  timeAggregate: TimeAggregate;
  period: Period;
  referenceCode: Reference;
  timeSlot: TimeSlot;
  trainingMode: boolean;
  kpiTab: KPITab;
  canRequestReports: boolean;
}

function useLegacyOrdersAggregated({
  timeAggregate,
  period,
  referenceCode,
  timeSlot,
  trainingMode,
  kpiTab,
  canRequestReports,
}: UseLegacyOrdersAggregatedParam) {
  const periodAsString = period.serialize();
  const timeSlotAsString = timeSlot.serialize();
  const dispatch = useDispatch();

  // Will trigger call(s) to getChart for kpi of current tab
  useQuery(
    [
      'legacyRequestCharts',
      {
        canRequestReports,
        kpiTab,
        referenceCode,
        trainingMode,
        timeAggregate,
        periodAsString,
        timeSlotAsString,
      },
    ],
    () => {
      if (canRequestReports) {
        dispatch(chartRequested({ kpi: tabToChartKPIMapping[kpiTab] }));
      }

      return true;
    },
    // override default value set in queryConfig
    // allways execute the query (but only once per page)
    // as the result is in the redux store
    // the api call will update the store state
    { staleTime: 0 },
  );

  const averageSpentsAggregated = {
    error: useSelector(getAverageSpentsError),
    isLoading: useSelector(areAverageSpentsLoading),
    data: useSelector(getAverageSpentsChartData),
  };
  const customersCountsAggregate = {
    error: useSelector(getCustomersCountsError),
    isLoading: useSelector(areCustomersCountsLoading),
    data: useSelector(getCustomersCountsChartData),
  };
  const revenuesAggregated = {
    error: useSelector(getRevenuesError),
    isLoading: useSelector(areRevenuesLoading),
    data: useSelector(getRevenuesChartData),
  };

  return { averageSpentsAggregated, customersCountsAggregate, revenuesAggregated };
}

export default useLegacyOrdersAggregated;
